import { DocumentStateType } from '../../contexts/DocumentContext';
import { DocumentPayload } from '../api/document-interface.types';
import { DocumentTemplateType, DocumentType } from '../../components/document-wizard/document.types';
import { CounterpartyPermissionLevel, permissionLevelToBooleans } from '../../types/counterparty-permissions';

const validateDocumentBody = (documentId: string, document: DocumentType): DocumentType => {
  if (typeof document?.body !== 'string') {
    console.error(`Wrong document body: ${document?.body}, documentId: ${documentId}`);
    return { ...document, body: '' };
  }
  return document;
};

const validateDocumentTemplateBody = (documentId: string, template: DocumentTemplateType): DocumentType => {
  if (typeof template?.body !== 'string') {
    console.error(`Wrong document body: ${template?.body}, documentId: ${documentId}`);
    return { ...template, body: '' };
  }
  return template;
};

/**
 * Pure function that converts a GraphQL document response into a partial DocumentStateType.
 * Replaces the 25+ dispatch fan-out in dispatchDocumentResponseToStore.
 */
export function documentStateFromResponse(
  response: DocumentPayload,
  updateDocument = true
): Partial<DocumentStateType> {
  const state: Partial<DocumentStateType> = {};

  if (updateDocument) {
    if (response.document) {
      state.document = validateDocumentBody(response.id, response.document);
    }
    if (response.template || response.workingTemplate) {
      const templateToUse = response.workingTemplate || response.template;
      state.template = validateDocumentTemplateBody(response.id, templateToUse);
    }
  }

  if (response.name || response.title) {
    const responseName = response.name || response.title;
    state.title = responseName;
    state.name = responseName;
  }

  state.id = response.id;
  state.status = response.status;
  state.currentTeamPossession = response.currentTeamPossession;
  state.lastUpdated = response.lastUpdated;
  state.documentSuggestions = response.documentSuggestions;
  state.signers = response.signers;
  state.eversignStatus = response.eversignStatus;
  state.eversignPdfUrl = response.eversignPdfUrl || null;
  state.signedPdfUrl = response.signedPdfUrl || null;

  // Only update approval request if explicitly provided
  if (response.hasOwnProperty('approvalRequest')) {
    state.approvalRequest = response.approvalRequest || null;
  }

  state.isEditable = response.isEditable || false;
  state.isTitleEditable = response.isTitleEditable || false;

  if (typeof response.isEligibleForSigningPrep === 'boolean') {
    state.isEligibleForSigningPrep = response.isEligibleForSigningPrep || false;
  }

  // Set counterparty permission level and sync legacy booleans
  const permLevel = (response.counterpartyPermissionLevel || 'COMMENT_ONLY') as CounterpartyPermissionLevel;
  state.counterpartyPermissionLevel = permLevel;
  const { receiverCollaborationEnabled, receiverPagePdfEditingEnabled } = permissionLevelToBooleans(permLevel);
  state.receiverCollaborationEnabled = receiverCollaborationEnabled;
  state.receiverPagePdfEditingEnabled = receiverPagePdfEditingEnabled;

  state.restrictedEditingMode = response.editorConfiguration?.restricted_editing || false;

  // Track changes: enabled for collaborative review states
  const isCollaborativeState =
    ['reviewing', 'shared'].includes(response.status) ||
    (response.status === 'Approval Pending' && response.receiverTeam !== null);
  state.trackChangesMode = isCollaborativeState ? true : response.editorConfiguration?.track_changes || false;

  state.shouldReturnForReceiverReview = response.shouldReturnForReceiverReview;
  state.source = response.source;
  state.useSignerOrder = response.useSignerOrder;
  state.importedAssetUrl = response.importedAssetUrl;
  state.contentType = response.importedAssetContentType;
  state.generatedDocumentPdfUrl = response.generatedDocumentPdfUrl;

  if (response.team) {
    state.teamId = response.team.id;
  }

  if (response.receiverTeam) {
    state.receiverTeamId = response.receiverTeam.id;
  }

  return state;
}
